import { useEffect, useRef, useState } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { apiUrl } from '../../schema/Environment';

const WS_PATH = '/api/democracy/ws';

const subscriptionsByTopic = new Map();
let stompClient = null;
let connecting = false;
const pendingSubscriptions = [];

let wsConnected = false;
const connectionListeners = new Set();
function notifyConnection() {
    connectionListeners.forEach((l) => { try { l(wsConnected); } catch (e) { /* ignore */ } });
}

function ensureClient() {
    if (stompClient) return;
    if (connecting) return;
    connecting = true;

    stompClient = new Client({
        webSocketFactory: () => new SockJS(apiUrl + WS_PATH),
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        debug: () => {},
        beforeConnect: () => {
            // STOMP CONNECT должен нести JWT — иначе бэк отклоняет (1002, Missing Authorization).
            const token = sessionStorage.getItem('token');
            stompClient.connectHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        },
        onConnect: () => {
            connecting = false;
            wsConnected = true;
            notifyConnection();
            console.info('[demo-ws] STOMP connected'); // DEBUG (убрать после диагностики)
            subscriptionsByTopic.forEach((entry, topic) => {
                attachStompSubscription(topic, entry);
            });
            while (pendingSubscriptions.length > 0) {
                const { topic, entry } = pendingSubscriptions.shift();
                attachStompSubscription(topic, entry);
            }
        },
        onStompError: (frame) => {
            console.error('[demo-ws] STOMP error:', frame.headers && frame.headers.message);
        },
        onWebSocketClose: (e) => {
            wsConnected = false;
            notifyConnection();
            console.warn('[demo-ws] socket closed', e && e.code, e && e.reason); // DEBUG
            subscriptionsByTopic.forEach((entry) => {
                entry.stompSubscription = null;
            });
        },
    });

    stompClient.activate();
}

function attachStompSubscription(topic, entry) {
    if (!stompClient || !stompClient.connected) return;
    if (entry.stompSubscription) return;
    entry.stompSubscription = stompClient.subscribe(topic, (message) => {
        let parsed;
        try {
            parsed = JSON.parse(message.body);
        } catch (e) {
            console.error('Failed to parse WS message body', e);
            return;
        }
        const handlersByType = entry.handlersByType;
        const eventType = parsed && parsed.type;
        const handlersForType = eventType && handlersByType.get(eventType);
        console.info('[demo-ws] recv', topic, eventType, handlersForType ? '(handled)' : '(no-handler)'); // DEBUG
        if (!handlersForType) return;
        handlersForType.forEach((handler) => {
            try {
                handler(parsed.payload, parsed);
            } catch (e) {
                console.error('WS handler threw', e);
            }
        });
    });
}

function addHandler(topic, type, handler) {
    let entry = subscriptionsByTopic.get(topic);
    if (!entry) {
        entry = { handlersByType: new Map(), stompSubscription: null };
        subscriptionsByTopic.set(topic, entry);
    }
    let handlersForType = entry.handlersByType.get(type);
    if (!handlersForType) {
        handlersForType = new Set();
        entry.handlersByType.set(type, handlersForType);
    }
    handlersForType.add(handler);

    if (stompClient && stompClient.connected) {
        attachStompSubscription(topic, entry);
    } else {
        pendingSubscriptions.push({ topic, entry });
    }

    return () => removeHandler(topic, type, handler);
}

function removeHandler(topic, type, handler) {
    const entry = subscriptionsByTopic.get(topic);
    if (!entry) return;
    const handlersForType = entry.handlersByType.get(type);
    if (handlersForType) {
        handlersForType.delete(handler);
        if (handlersForType.size === 0) {
            entry.handlersByType.delete(type);
        }
    }
    if (entry.handlersByType.size === 0) {
        if (entry.stompSubscription) {
            try {
                entry.stompSubscription.unsubscribe();
            } catch (e) {
                // ignore
            }
        }
        subscriptionsByTopic.delete(topic);
    }
}

// Состояние WS-коннекта для UI (баннер «связь потеряна»).
export function useDemocracyWsConnected() {
    const [connected, setConnected] = useState(wsConnected);
    useEffect(() => {
        connectionListeners.add(setConnected);
        setConnected(wsConnected);
        return () => connectionListeners.delete(setConnected);
    }, []);
    return connected;
}

export default function useDemocracyWs(topic, type, handler) {
    // Хэндлер держим в ref, чтобы подписка не пересоздавалась на каждый рендер
    // (инлайновые колбэки меняют идентичность → раньше шёл unsub/resub каждый рендер).
    const handlerRef = useRef(handler);
    useEffect(() => { handlerRef.current = handler; }, [handler]);

    useEffect(() => {
        if (!topic || !type) return undefined;
        ensureClient();
        const stableHandler = (payload, raw) => {
            if (handlerRef.current) handlerRef.current(payload, raw);
        };
        const dispose = addHandler(topic, type, stableHandler);
        return dispose;
    }, [topic, type]);
}
